import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';
import { CompanyService } from 'app/modules/components/configuration/company/company.service.service';

/**
 * Caché liviano del catálogo de compañías para resolver el nombre cuando el
 * log de integración trae la compañía solo con id (sin nombre embebido). Se
 * carga una vez por sesión desde `findToComboBox` y resuelve por `_id` (Mongo)
 * o `id` (numérico).
 */
@Injectable({ providedIn: 'root' })
export class IntegrationErrorsCompanyLookupService {
  private readonly companyService = inject(CompanyService);
  private readonly byId = new Map<string, string>();
  private load$?: Observable<void>;

  /**
   * Carga el catálogo una única vez y completa cuando el mapa está listo,
   * para que el consumidor pueda encadenar el fetch de filas y las celdas no
   * rendericen antes de tener los nombres. Ante error completa igual (las
   * celdas caen al fallback) y el próximo llamado reintenta.
   */
  ensureLoaded(): Observable<void> {
    if (!this.load$) {
      this.load$ = this.companyService.findToComboBox().pipe(
        take(1),
        tap((res) => {
          for (const c of res?.data ?? []) {
            if (c._id) this.byId.set(String(c._id), c.name);
            if (c.id != null) this.byId.set(String(c.id), c.name);
          }
        }),
        map(() => void 0),
        catchError(() => {
          this.load$ = undefined;
          return of(void 0);
        }),
        shareReplay(1)
      );
    }
    return this.load$;
  }

  nameById(id: unknown): string | undefined {
    if (id == null) return undefined;
    return this.byId.get(String(id));
  }
}
